import { ProductInterface } from '../../../services/exports/Interfaces';
import classnames from 'classnames';
import CustomLabel from '../../labels/CustomLabel';
import Theme from '../../Theme';
import PriceComparison from '../../menu/PriceComparison';
import { useTranslation } from 'react-i18next';
import { PRODUCT_HEIGHT } from '../../../services/exports/Constants';
import { useMemo } from 'react';
import useProductIdentifiers from '../../../hooks/menu/useProductIdentifiers';
import useBasket from '../../../hooks/basket/useBasket';
import SeoResource from '../../../services/resources/SeoResource';
import { Radio } from '@mui/material';

interface Props {
  product: ProductInterface;
  showCaseProduct?: boolean;
  toggleProduct: () => void;
  className?: string;
}

export default function SingleSelectProduct(props: Props) {
  const { t } = useTranslation(null, {
    keyPrefix: 'Components:Menu:SingleSelectMenuItems',
  });

  const { product, showCaseProduct, toggleProduct, className } = props;

  const { emojis } = useProductIdentifiers(product);
  const { freeItem } = useBasket();

  const isSelected = useMemo(() => freeItem?.product_id === product?.id, [freeItem?.product_id, product?.id]);

  function renderSoldOutOverlay() {
    return (
      <div className={soldOutOverlayContainer}>
        {!product?.thumbnail_url && (
          <p className="text-white absolute top-1/2 -translate-y-1/2 left-5">{t('labels.sold_out')}</p>
        )}
      </div>
    );
  }

  function renderPriceRow(): JSX.Element {
    return (
      <div className={priceRowContainer}>
        <div className="h-full flex flex-col justify-end">
          <PriceComparison actualPrice={product?.actual_price} originalPrice={product?.original_price} />
        </div>
        <div className={customLabelContainer}>
          {emojis.isNotEmpty() && <p className="mb-xs text-right">{emojis.implode(' ')}</p>}
          <CustomLabel
            className="text-xs"
            backgroundColor={Theme.color.green['300']}
            textColor={Theme.color.primary}
            label={t('labels.free')}
          />
        </div>
      </div>
    );
  }

  function renderProductImage(): JSX.Element {
    return (
      product?.thumbnail_url && (
        <div className={productImageContainer}>
          <div className={productImage}>
            <img
              src={product?.thumbnail_url}
              alt={SeoResource.toAltText(`${product?.name} image`)}
              className={productImage}
            />
          </div>
          {!product?.is_available && (
            <p className="text-white absolute top-1/2 -translate-y-1/2 w-full text-center z-10">
              {t('labels.sold_out')}
            </p>
          )}
        </div>
      )
    );
  }

  return (
    <label
      className={classnames(container(showCaseProduct), className)}
      style={{ height: PRODUCT_HEIGHT }}
      onClick={toggleProduct}
    >
      {renderProductImage()}
      <div className={wrapper(showCaseProduct)}>
        <div className={infoContainer}>
          <div className={infoWrapper}>
            <div className="grid grid-cols-5">
              <div className="col-span-4">
                <h6 className={titleStyle}>{product?.name}</h6>
              </div>
              <div className="cols-span-1 flex justify-end">
                {!showCaseProduct && (
                  <div className="flex-none">
                    <Radio
                      checked={isSelected}
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: 25,
                        },
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1">
              <p className="text-gray-700 line-clamp-2 break-words text-sm">{product?.description}</p>
            </div>
          </div>
        </div>
        {!showCaseProduct && renderPriceRow()}
      </div>

      {!product?.is_available && renderSoldOutOverlay()}
    </label>
  );
}

const container = (showCaseProduct?: boolean) =>
  `w-full border rounded-2xl flex flex-row ${!showCaseProduct ? 'cursor-pointer' : ''} overflow-hidden`;

const wrapper = (showCaseProduct: boolean) =>
  `p-md flex flex-col flex-1 ${showCaseProduct ? 'justify-center' : 'justify-between'} relative`;

const titleStyle = 'text-black line-clamp-2 break-words mb-2xs mr-2xs';

const productImageContainer = 'flex aspect-square overflow-hidden lg:py-none lg:pl-none py-md pl-xs relative';
const productImage =
  'relative h-fit max-h-[100%] w-full my-auto aspect-square bg-gray-100 lg:rounded-tl-lg lg:rounded-bl-lg lg:rounded-tr-none lg:rounded-br-none md:rounded-lg sm:rounded-lg object-cover';

const soldOutOverlayContainer = 'top-0 left-0 absolute h-full w-full bg-black bg-opacity-50';

const priceRowContainer = 'flex flex-row justify-between items-center w-full relative';

const customLabelContainer = 'flex flex-col';

const infoContainer = 'flex flex-row justify-between';

const infoWrapper = 'flex flex-1 flex-col';
